import {
  Badge,
  Button,
  DashboardCard,
  DashboardGrid,
  PageHeader,
  Separator,
  StatusBadge,
  cn,
} from '@zafroi/ui-kit';
import {
  ArrowLeft,
  BookOpen,
  Bot,
  ExternalLink,
  LifeBuoy,
  UserSquare,
} from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  useConversation,
  useConversationMessages,
  useCustomer,
} from '@/hooks/useEngageQueries';
import { QueryStateGuard } from '@/components/common/QueryStateGuard';
import {
  channelKindLabel,
  conversationStatusVariants,
} from '@/types/status-variants';
import { relativeTime, shortDateTime } from '@/lib/format';
import type { ConversationMessage } from '@/types/engage';

export function ConversationDetailPage() {
  const navigate = useNavigate();
  const { conversationId } = useParams<{ conversationId: string }>();
  const conversationQuery = useConversation(conversationId);
  const messagesQuery = useConversationMessages(conversationId);
  const customerQuery = useCustomer(conversationQuery.data?.customerId);

  return (
    <QueryStateGuard
      data={conversationQuery.data}
      isLoading={conversationQuery.isLoading}
      isError={conversationQuery.isError}
      emptyTitle="Conversation not found"
      emptyDescription="This thread may have been archived or never existed."
    >
      {(conv) => (
        <div className="space-y-5">
          <PageHeader
            eyebrow={
              <button
                type="button"
                className="inline-flex items-center gap-1 hover:text-foreground"
                onClick={() => navigate('/conversations')}
              >
                <ArrowLeft className="h-3 w-3" />
                Inbox
              </button>
            }
            title={conv.subject ?? conv.customerName}
            description={conv.subject ? conv.customerName : undefined}
            actions={
              <Button asChild variant="outline" size="sm">
                <Link to="/conversations/guide">
                  <BookOpen className="h-3.5 w-3.5" />
                  Help &amp; Guide
                </Link>
              </Button>
            }
            meta={
              <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  Channel
                  <Badge tone="neutral" withDot={false}>
                    {channelKindLabel[conv.channel] ?? conv.channel}
                  </Badge>
                </span>
                <Separator orientation="vertical" className="h-4" />
                <span className="inline-flex items-center gap-1.5">
                  Status
                  <StatusBadge
                    status={conv.status}
                    variants={conversationStatusVariants}
                  />
                </span>
                <Separator orientation="vertical" className="h-4" />
                <span>
                  Assignee ·{' '}
                  <span className="text-foreground">
                    {conv.assigneeName ?? 'Unassigned'}
                  </span>
                </span>
                <Separator orientation="vertical" className="h-4" />
                <span>
                  Last message{' '}
                  <span className="text-foreground">
                    {shortDateTime(conv.lastMessageAt)}
                  </span>
                </span>
              </div>
            }
          />

          <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
            <DashboardCard
              title="Timeline"
              description="Every message that's been exchanged with this customer on this channel — in order."
              flushBody
            >
              <div className="space-y-3 p-5">
                {(messagesQuery.data ?? []).map((message) => (
                  <MessageBubble key={message.id} message={message} />
                ))}
                {(messagesQuery.data ?? []).length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    No messages on this thread yet.
                  </p>
                )}
              </div>
            </DashboardCard>

            <div className="space-y-5">
              <DashboardCard title="Customer" icon={UserSquare}>
                {customerQuery.data ? (
                  <>
                    <div className="text-sm font-medium text-foreground">
                      {customerQuery.data.name}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {customerQuery.data.company ?? 'Individual'}
                    </div>
                    <div className="mt-3 space-y-1.5 text-xs">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-muted-foreground">Phone</span>
                        <span className="text-foreground">
                          {customerQuery.data.phone ?? '—'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-muted-foreground">Email</span>
                        <span className="text-foreground">
                          {customerQuery.data.email ?? '—'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-muted-foreground">Conversations</span>
                        <span className="tnum text-foreground">
                          {customerQuery.data.totalConversations}
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-4 w-full"
                      onClick={() =>
                        navigate(`/customers/${customerQuery.data!.id}`)
                      }
                    >
                      Open profile
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Button>
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Customer record unavailable.
                  </p>
                )}
              </DashboardCard>

              <DashboardCard title="Need to reply?" icon={LifeBuoy}>
                <p className="text-xs text-muted-foreground">
                  This page is read-only today — it shows the conversation
                  history. To reply to the customer, find the matching
                  ticket in the Support queue and use its reply composer.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3 w-full"
                  onClick={() => navigate('/support')}
                >
                  Open Support queue
                  <ExternalLink className="h-3.5 w-3.5" />
                </Button>
                <p className="mt-3 text-[11px] text-muted-foreground">
                  Replying from inside this page (and internal notes) lands
                  on the roadmap — see the Conversations guide.
                </p>
              </DashboardCard>

              <DashboardGrid columns={2}>
                <div className="rounded-lg border border-border bg-card px-3 py-2.5">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Messages
                  </div>
                  <div className="mt-1 text-lg font-semibold tnum text-foreground">
                    {conv.messageCount}
                  </div>
                </div>
                <div className="rounded-lg border border-border bg-card px-3 py-2.5">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Last touch
                  </div>
                  <div className="mt-1 text-sm text-foreground">
                    {relativeTime(conv.lastMessageAt)}
                  </div>
                </div>
              </DashboardGrid>
            </div>
          </div>
        </div>
      )}
    </QueryStateGuard>
  );
}

function MessageBubble({ message }: { message: ConversationMessage }) {
  if (message.direction === 'internal') {
    return (
      <div className="flex flex-col gap-1 rounded-md border border-warning/30 bg-warning/5 px-3 py-2">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-warning">
          <span>Internal note · {message.authorName ?? 'Team'}</span>
          <span>{relativeTime(message.at)}</span>
        </div>
        <p className="text-sm text-foreground">{message.body}</p>
      </div>
    );
  }
  const outbound = message.direction === 'outbound';
  // Bot turns persist with authorName "Bot" (see EngageBotApiService.recordBotTurn).
  // Render them distinctly so the agent can tell apart "bot reply" from "agent reply".
  const isBot =
    outbound && (message.authorName ?? '').toLowerCase() === 'bot';
  const isAutoRespond =
    outbound && /auto[-\s]?respond/i.test(message.authorName ?? '');

  return (
    <div className={cn('flex', outbound ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[78%] rounded-lg px-3 py-2 text-sm shadow-elev-1',
          !outbound && 'bg-card text-foreground',
          outbound && isBot && 'border border-info/30 bg-info/10 text-foreground',
          outbound && isAutoRespond && 'border border-muted bg-muted/40 text-foreground',
          outbound && !isBot && !isAutoRespond && 'bg-primary/10 text-foreground',
        )}
      >
        <div className="flex items-center justify-between gap-3 text-[10px] uppercase tracking-wider text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            {isBot && <Bot className="h-3 w-3" />}
            {message.authorName ?? (outbound ? 'You' : 'Customer')}
            {isAutoRespond && (
              <span className="rounded-sm border border-muted-foreground/40 px-1 text-[9px]">
                Auto
              </span>
            )}
          </span>
          <span className="tnum">{relativeTime(message.at)}</span>
        </div>
        <p className="mt-0.5 whitespace-pre-wrap break-words">{message.body}</p>
      </div>
    </div>
  );
}
